import queryString from 'query-string';

import isEmpty from 'Lib/utilities/isEmpty';
import { applyBranding } from 'Lib/white-labeler';

import * as actions from './actions';
import * as storage from './storage';

import agent from '~/agent';
import store from '~/store';

export const initialize = () => {
	agent.request
		.get('init')
		.then(response => {
			// Check for Redirect to Primary Subdomain (202)
			if (response.status == 202 && response.data.redirectTo) {
				window.location.href = `https://${response.data.redirectTo}${process.env.DOMAIN}${window.location.pathname}${window.location.search}`;

				return;
			}

			/* 
				URLs to test lease + portal switching
				________________________________________________________________________

				Portal 1 (Jack Smitty) w/o Lease
				https://abchomes.rentvinedev.com/portals/resident/summary?switchPortalID=378
				
				Portal 1 (Jack Smitty) w/ Lease 1 (1112 Foster Street)
				https://abchomes.rentvinedev.com/portals/resident/summary?switchPortalID=378&switchLeaseID=522

				Portal 1 (Jack Smitty) w/ Lease 2 (6570 Briarcliff Road)
				https://abchomes.rentvinedev.com/portals/resident/summary?switchPortalID=378&switchLeaseID=573

				Portal 2 (Chris Chat) w/o Lease
				https://abchomes.rentvinedev.com/portals/resident/summary?switchPortalID=1989

				Portal 2 (Chris Chat) w/ Lease 1 (123 No Deposit Ln)
				https://abchomes.rentvinedev.com/portals/resident/summary?switchPortalID=1989&switchLeaseID=392

				Portal 2 (Chris Chat) w/ Lease 2 (123 Security Deposit Road)
				https://abchomes.rentvinedev.com/portals/resident/summary?switchPortalID=1989&switchLeaseID=393
			*/

			const url = new URL(window.location);

			const defaultPortal = storage.getPortal();
			const defaultLease = storage.getLease();

			const { switchPortalID, switchLeaseID } = queryString.parse(window.location.search);
			const { portals, portalAccounts, user, account, residentSettings, brandSettings, company } = response.data;

			let portal = null;

			// If switch portal is present in URL, set the portal to that if matched
			if (switchPortalID) {
				portal = portals.find(item => item.tenant.contactID == switchPortalID);

				if (portal) {
					storage.setPortal(portal.tenant.contactID);
				}
			} else if (defaultPortal) {
				// If default portal is set, and portal is not matched, set the default portal
				portal = portals.find(item => item.tenant.contactID == defaultPortal);
			}

			if (!portal) {
				portal = portals.length == 1 ? portals[0] : null;

				// If portal is not matched, automatically set the first portal
				if (portal) {
					storage.setPortal(portal.tenant.contactID);
				}
			}

			let lease = null;

			if (portal) {
				// If switch lease is present in URL, set the lease to that if matched
				if (switchLeaseID) {
					lease = portal.leases.find(item => item.lease.leaseID == switchLeaseID);

					if (lease) {
						storage.setLease(lease.lease.leaseID);
					}
				} else if (defaultLease && !lease) {
					// If default lease is set, and lease is not matched, set the default lease
					lease = portal.leases.find(item => item.lease.leaseID == defaultLease);
				}

				// If lease is not matched, automatically set the first lease
				if (!lease) {
					lease = portal && !isEmpty(portal.leases) ? portal.leases[0] : null;

					if (lease) {
						storage.setLease(lease.lease.leaseID);
					}
				}
			}

			// Clear switch params from url
			if (switchPortalID || switchLeaseID) {
				url.searchParams.delete('switchPortalID');
				url.searchParams.delete('switchLeaseID');
				history.pushState({}, '', url);
			}

			applyBranding(`https://cdn${process.env.DOMAIN}/${account.directory}/branding/${brandSettings.webIconFileName}`);

			store.dispatch(
				actions.init({
					isInitialized: true,
					hasError: false,
					errorMessage: null,
					user,
					portals,
					portalAccounts,
					portal,
					account,
					lease,
					changePortal: portal ? false : true,
					changeLease: lease ? false : true,
					isImpersonated: response.data.impersonation ? true : false,
					impersonation: response.data.impersonation,
					residentSettings,
					brandSettings,
					company
				})
			);
		})
		.then(() => {
			const { lease } = store.getState().app;
			console.log('lease', lease);
			getLease(lease?.lease?.leaseID);
		})
		.catch(error => {
			if (error.response && error.response.status == 401) {
				store.dispatch(
					actions.init({
						isInitialized: false,
						hasError: false,
						doLogin: true,
						errorMessage: 'Could not initialize the application.'
					})
				);
			} else {
				store.dispatch(
					actions.init({
						hasAuthenticationError: true,
						isInitialized: false,
						hasError: true,
						errorMessage: 'Could not initialize the application.'
					})
				);
			}
		});
};

export const getLeases = () => {
	agent.request.get('leases', { includes: 'unit' }).then(response => {
		const leases = response.data;
		let lease = false;
		let unit = false;

		if (leases.length == 1) {
			lease = leases[0].lease;
			unit = leases[0].unit;
		}

		store.dispatch(actions.getLeases(leases, lease, unit));
	});
};

export const getLease = leaseID => {
	agent.request
		.get('lease', { search: leaseID, includes: 'unit,rentersInsuranceFile,rentersInsuranceRequest,tenantLiabilityInsurance,leaseTenant' })
		.then(response => {
			store.dispatch(actions.setLease(response.data));
		})
		.catch(error => {
			if (error?.message?.includes('403') || error?.response?.status === 403) {
				portalDisabled();
			}
		});
};

export const changePortal = () => {
	store.dispatch(actions.changePortal());
};

export const cancelChangePortal = () => {
	store.dispatch(actions.cancelChangePortal());
};

export const setPortal = portal => {
	store.dispatch(actions.setPortal(portal));
};

export const changeLease = () => {
	store.dispatch(actions.changeLease());
};

export const cancelChangeLease = () => {
	store.dispatch(actions.cancelChangeLease());
};

export const setLease = lease => {
	store.dispatch(actions.setLease(lease));
	getLease();
};

export const portalDisabled = () => {
	store.dispatch(actions.portalDisabled());
};

/**
 * API
 */
export const callApi = (promise, exclude = false) => {
	if (!exclude) {
		store.dispatch(actions.startApiCall());
	}

	if (promise) {
		promise
			.catch?.(error => {
				// TODO: check the status code is 401
				if (error.response?.status == 401) {
					store.dispatch(actions.logout());
				}
			})
			.finally?.(() => {
				if (!exclude) {
					store.dispatch(actions.endApiCall());
				}
			});
	}
};
